// src/components/Admin/ActasTable/ActasTable.js

import React from "react";
import { Link } from "react-router-dom";
import { Table, Button, Icon, Loader, Label } from "semantic-ui-react";
import "./ActasTable.scss";

/** Formateador estable para zona Bogotá */
const fmt = new Intl.DateTimeFormat("es-CO", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "America/Bogota",
});

/**
 * groupedMeetings: [
 *   {
 *     meetingId, meetingTitle, ciudad, centro,
 *     sessions: [ { id, start_datetime, end_datetime, audio_file, acta_status, meeting_title, meeting } ]
 *   },
 *   ...
 * ]
 */
export function ActasTable({
  groupedMeetings = [],
  loading,
  loadingSessionId,
  loadingMeetingId,
  onGenerateActaSession,
  // onPreviewActaSession, // ← ya no se usa (se navega con <Link>)
  onGenerateActaAuto,
}) {
  if (loading) {
    return <Loader active>Cargando reuniones y sesiones...</Loader>;
  }

  const formatDT = (iso) => {
    try {
      if (!iso) return "—";
      return fmt.format(new Date(iso));
    } catch {
      return "—";
    }
  };

  return (
    <div className="actas-table-wrapper">
      <Table celled className="actas-table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ width: "28%" }}>
              Reunión
            </Table.HeaderCell>
            <Table.HeaderCell>Sesiones</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 210 }}>Acciones</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {groupedMeetings.map((m) => {
            const total = m.sessions.length;
            const generadas = m.sessions.filter(
              (s) => s.acta_status === "generated"
            ).length;

            return (
              <Table.Row key={m.meetingId}>
                {/* Columna Reunión */}
                <Table.Cell>
                  <div className="meeting-head">
                    <div className="title">{m.meetingTitle}</div>
                    <div className="meta">
                      {m.ciudad && <span className="chip">{m.ciudad}</span>}
                      {m.centro && <span className="chip">{m.centro}</span>}
                      <span className="chip neutral">
                        {total} sesión{total !== 1 ? "es" : ""}
                      </span>
                      <span
                        className={`chip ${
                          generadas === total && total > 0 ? "ok" : "warn"
                        }`}
                      >
                        {generadas}/{total} actas
                      </span>
                    </div>
                  </div>
                </Table.Cell>

                {/* Columna Sesiones: todas dentro de la misma fila */}
                <Table.Cell>
                  <div className="sessions-inline">
                    {m.sessions.map((s, i) => (
                      <div key={s.id} className="session-pill">
                        <div className="line1">
                          <Label size="mini" circular>
                            {i + 1}
                          </Label>
                          <span className="dt">
                            {formatDT(s.start_datetime)} {" — "}{" "}
                            {formatDT(s.end_datetime)}
                          </span>
                        </div>

                        <div className="line2">
                          {s.audio_file ? (
                            <span className="audio ok" title="Audio adjunto">
                              <Icon name="volume up" /> Audio
                            </span>
                          ) : (
                            <span className="audio none" title="Sin audio">
                              <Icon name="ban" /> Sin audio
                            </span>
                          )}

                          {s.acta_status === "generated" ? (
                            <span
                              className="status generated"
                              title="Acta generada"
                            >
                              <Icon name="check" /> Generada
                            </span>
                          ) : (
                            <span
                              className="status pending"
                              title="Acta pendiente"
                            >
                              <Icon name="clock" /> Pendiente
                            </span>
                          )}
                        </div>

                        <div className="line3 actions">
                          <Button
                            size="mini"
                            color="green"
                            icon
                            labelPosition="left"
                            loading={loadingSessionId === s.id}
                            disabled={
                              !s.audio_file ||
                              s.acta_status === "generated" ||
                              loadingSessionId === s.id
                            }
                            onClick={() => onGenerateActaSession(s)}
                            title={
                              !s.audio_file
                                ? "No hay audio en esta sesión"
                                : s.acta_status === "generated"
                                ? "Ya generada"
                                : "Generar acta de la sesión"
                            }
                          >
                            <Icon name="magic" />
                            Generar
                          </Button>

                          <Button
                            as={Link}
                            to={`/app/actas/${s.id}/editar`}
                            size="mini"
                            color="blue"
                            disabled={s.acta_status !== "generated"}
                            title={
                              s.acta_status !== "generated"
                                ? "Primero genera el acta"
                                : "Ver vista previa de la sesión"
                            }
                          >
                            <Icon name="eye" /> Vista previa
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Table.Cell>

                {/* Columna Acciones (Consolidado/Auto) */}
                <Table.Cell textAlign="center">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                   <h3>Nota: primero debe Generar el Acta para poder Visualizar la Acta Consolidada</h3>
	            <Button
                      size="small"
                      color="violet"
                      icon
                      labelPosition="left"
                      loading={loadingMeetingId === m.meetingId}
                      disabled={
                        loadingMeetingId === m.meetingId ||
                        m.sessions.length === 0
                      }
                      onClick={() => onGenerateActaAuto(m.meetingId)}
                      style={{ minWidth: 210 }}
                      title={
                        m.sessions.length === 0
                          ? "Esta reunión no tiene sesiones"
                          : "Generar automáticamente (sesión única o consolidado)"
                      }
                    >
                      <Icon name="clone" />
                      1 -  Generar Acta (Auto)
                    </Button>

                    <Button
                      as={Link}
                      to={`/app/actas/consolidado/${m.meetingId}`}
                      size="small"
                      color="blue"
                      icon
                      labelPosition="left"
                      style={{ minWidth: 210 }}
                      title="Ver última versión consolidada persistida"
                    >
                      <Icon name="eye" />
                      2 -  Vista previa (consolidada)
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}
